
import Vue from 'vue';
import Component from 'vue-class-component';
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import {Prop} from 'vue-property-decorator';
import {
    CertificateDetailsModel,
    CertificatesActions,
    CertificatesClient,
    CertificateStatus,
    ICertificatesActions,
    IInvalidationRequest,
        ManualInvalidationReason,
    InvalidationRequest, MinimalDocumentModel
} from "@/api-client";
import CertificateDetailsContent from "@/features/certificates/CertificateDetailsContent.vue";
import DetailsLayout from "@/features/personProfile/components/DetailsLayout.vue";
import RejectionDetails from "@/features/shared/components/RejectionDetails.vue";
import CertificateStatusBadge from "@/features/shared/components/CertificateStatusBadge.vue";
import {IApprovalActions} from "@/features/shared/components/sections/ApprovalActions";
import ApprovalActions from "@/features/shared/components/sections/ApprovalActions.vue";
import toasts from "@/components/Toasts";
import AdditionalInfoButton from "@/features/shared/components/AdditionalInfoButton.vue";
import LabelOptional from "@/features/shared/components/LabelOptional.vue";
import ButtonWithConfirm from "@/features/shared/components/ButtonWithConfirm.vue";
import DuplicationDetails from "@/features/shared/components/DuplicationDetails.vue";
import DocumentValidity from "@/features/documents/query/components/DocumentValidity.vue";
import MinimalDocumentDetails from "@/features/documents/MinimalDocumentDetails.vue";
import {NavigationGuardNext} from "vue-router/types/router";
import DocumentDigitalRepresentationModal from "@/features/shared/components/DocumentDigitalRepresentationModal.vue";
import renderCertificatePrintTemplate from "@/features/shared/CertificatePrintService";
import QrCode from "@/components/QrCode.vue";
import DocumentValidityQueryTimestamp from "@/features/shared/components/DocumentValidityQueryTimestamp.vue";
import waitForAllImagesLoaded from "@/components/WaitForImagesLoaded";

const accessTokenStorageKey = 'mis-certificate-access-token'

interface Props {
    preloadedCertificate: CertificateDetailsModel | MinimalDocumentModel;
    preLoadedActions: CertificatesActions;
    id?: string;
    preLoadedAdditionalInfo?: string;
    accessToken?: string;
}

@Component({
    components: {
        QrCode,
        DocumentValidity,
        DetailsLayout,
        SectionPageHeading,
        HeadingGroup,
        CertificateDetailsContent,
        ApprovalActions,
        CertificateStatusBadge,
        RejectionDetails,
        AdditionalInfoButton,
        LabelOptional,
        ButtonWithConfirm,
        DuplicationDetails,
        MinimalDocumentDetails,
        DocumentDigitalRepresentationModal,
        DocumentValidityQueryTimestamp
    },
    async beforeRouteEnter(to, from, next: NavigationGuardNext<CertificateDetails>) {
        const props = to.meta!.props as Props;
        try {
            // This code is duplicated in MedicalCertificate.vue, CertificateDetails.vue and EndorsementDetails.vue.
            if (to.params.accessToken) {
                // Save token to session storage and replace location without token to prevent token from being preserved in history
                const urlWithoutToken = to.fullPath.replace(to.params.accessToken, "");
                sessionStorage.setItem(accessTokenStorageKey, to.params.accessToken as string);
                await window.location.replace(urlWithoutToken);
                return;
            }

            const accessToken = sessionStorage.getItem(accessTokenStorageKey);
            // Access token is not removed from session storage to enable language change and login when querying certificate by access token.

            if (!to.params.certificateId && !to.params.id && !accessToken) {
                await this.$router.push({ name: "not-found" });
                return;
            }

            // Either id OR access token is required
            const certificate = to.params.id || to.params.certificateId
                ? await new CertificatesClient().details(to.params.certificateId || to.params.id)
                : await new CertificatesClient().detailsByAccessToken(accessToken!);

            props.preloadedCertificate = certificate;
            props.id = certificate.id;

            // Only assign if no id was given to be able to determine whether the query was a public or a private one.
            props.accessToken = !to.params.id && !to.params.certificateId ? accessToken! : "";

            props.preLoadedActions = await new CertificatesClient().availableActions(certificate.id);
            if (props.preLoadedActions.canSetAdditionalInfo) {
                props.preLoadedAdditionalInfo = await new CertificatesClient().additionalInfo(certificate.id);
            }

            next();
        } catch (e) {
            next(e);
        }
    }
})
export default class CertificateDetails extends Vue implements Props {
    static accessTokenStorageKey = accessTokenStorageKey;

    @Prop({required: true})
    preloadedCertificate!: CertificateDetailsModel | MinimalDocumentModel;
    @Prop({required: true})
    preLoadedActions!: CertificatesActions;
    @Prop({default: () => ""})
    id!: string;
    @Prop({default: ""})
    preLoadedAdditionalInfo!: string;
    @Prop({default: true})
    addContainerCss!: boolean;
    @Prop({default: ""})
    accessToken!: string;

    actions: ICertificatesActions = this.preLoadedActions;
    additionalInfo = this.preLoadedAdditionalInfo;
    certificate: CertificateDetailsModel | MinimalDocumentModel = this.preloadedCertificate;

    statuses = CertificateStatus;
    $refs!: {
        digitalRepresentationModal: DocumentDigitalRepresentationModal,
        qrCode: QrCode;
    }

    async refresh() {
        this.certificate = await new CertificatesClient().details(this.certificate.id);
        this.actions = await new CertificatesClient().availableActions(this.id);
    }

    get approvalActions(): IApprovalActions {
        return {
            canApprove: false,
            canDisapprove: this.actions.canInvalidate,
            canSuspend: false
        }
    }

    async invalidate(comment: string, reason: ManualInvalidationReason) {
        const invalidationRequest: IInvalidationRequest = {reason: reason, comment: comment}
        await new CertificatesClient().invalidate(this.certificate.id, new InvalidationRequest(invalidationRequest));
        await this.refresh();
    }

    async setAdditionalInfo() {
        new CertificatesClient()
            .setAdditionalInfo(this.id, this.additionalInfo)
            .then(() => toasts.success(this.$ls.Global_Toast_Success_AdditionalInfoSaved));
    }

    get areFullDetailsShown() {
        return this.certificate instanceof CertificateDetailsModel;
    }

    async print() {
        const printWindow = window.open()!;
        printWindow.document.open();
        printWindow.document.write(await this.getRenderedPrintTemplate());
        printWindow.document.close();
        printWindow.focus();
        await waitForAllImagesLoaded(printWindow);
        printWindow.print();
    }

    get canPrintOut(): boolean {
        return this.certificate instanceof CertificateDetailsModel
            && this.actions.canPrintOut;
    }

    get isPublicQuery(): boolean {
        return !!this.accessToken;
    }

    async openDigitalRepresentation() {
        this.$refs.digitalRepresentationModal.show(await this.getRenderedPrintTemplate(true));
    }

    async getRenderedPrintTemplate(transparentSignatures = false) {
        if (!this.areFullDetailsShown)
            throw Error("Only full details can be printed.");

        const printTemplate = this.isPublicQuery
            ? (await new CertificatesClient().printTemplateByAccessToken(this.accessToken))
            : (await new CertificatesClient().printTemplate(this.certificate.id));
        const signatureUrls = transparentSignatures
            ? {
                transparentSignatureUrl: printTemplate.transparentSignatureUrl,
                transparentOfficialSignatureUrl: printTemplate.transparentOfficialSignatureUrl
            }
            : {};
        return await renderCertificatePrintTemplate(this.certificate as CertificateDetailsModel, printTemplate.url, this.$refs.qrCode.getBase64QrCode(), printTemplate.frontUrl, printTemplate.backUrl, signatureUrls);
    }
}
